import {
  AnalyticsEvent,
  BorrowingPowerPageResultTypenames,
  BorrowingPowerPageSuccess,
  BorrowingPowerProductType,
  PinwheelExperienceInput,
  Typenames,
  VxlActionTypenames,
} from '@varo/graphql-schema-types';
import { Href, NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useBorrowingPowerPage from 'features/advance/hooks/useBorrowingPowerPage';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  parseGQLAnalyticsEvent,
  parseOptionalGQLAnalyticsEvent,
} from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedOrCoreError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

export interface ExtendedBorrowingPowerPageSuccess extends BorrowingPowerPageSuccess {
  borrowingPowerPageTitle: string;
}

const isExtendedBorrowingPowerPageSuccess = (
  page: BorrowingPowerPageSuccess,
): page is ExtendedBorrowingPowerPageSuccess => {
  return (
    page?.__typename ===
      BorrowingPowerPageResultTypenames.BorrowingPowerPageSuccess &&
    'borrowingPowerPageTitle' in page
  );
};

const IncreaseYourAdvanceLimits = () => {
  const router = useRouter();
  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const { borrowingPowerPage, error, fetching, refetch } =
    useBorrowingPowerPage(BorrowingPowerProductType.Advance);

  const { refetchDdManagementFlow, handleDdManagementLinkTokenFailure } =
    useDirectDepositManagementFlow();

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const formattedError = useFormattedCombinedOrCoreError(error);

  const { bottom } = useSafeAreaInsets();
  const { segmentClient } = useAnalyticsConfiguration();

  const isBorrowingPowerPageError =
    borrowingPowerPage?.__typename ===
    BorrowingPowerPageResultTypenames.BorrowingPowerPageError;

  const isBorrowingPowerSuccess =
    borrowingPowerPage?.__typename ===
    BorrowingPowerPageResultTypenames.BorrowingPowerPageSuccess;

  const analytics = parseOptionalGQLAnalyticsEvent(
    isBorrowingPowerSuccess ? borrowingPowerPage.displayAnalytics : undefined,
  );

  useViewDidFocusLogScreenAnalytics(analytics);

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {isBorrowingPowerSuccess ? (
        <View style={styles.parentContainer}>
          <ScrollView
            style={styles.parentContainer}
            contentContainerStyle={styles.scrollContainer}
            showsVerticalScrollIndicator={false}
          >
            {isExtendedBorrowingPowerPageSuccess(borrowingPowerPage) ? (
              <TitleLgText style={styles.titleText}>
                {borrowingPowerPage.borrowingPowerPageTitle}
              </TitleLgText>
            ) : null}
            <BodyBaseText style={styles.subTitleText}>
              {borrowingPowerPage.subTitle}
            </BodyBaseText>

            {borrowingPowerPage.content.length > 0 ? (
              <View>
                {borrowingPowerPage.content.map((content) => (
                  <View style={styles.contentList} key={content.id}>
                    {content.title ? (
                      <BodyBaseBoldText>{content.title}</BodyBaseBoldText>
                    ) : null}
                    {content.subTitle ? (
                      <BodyBaseText style={styles.contentListSubtitle}>
                        {content.subTitle}
                      </BodyBaseText>
                    ) : null}
                    {content.contentList &&
                    content.contentList.content?.length > 0 ? (
                      <ContentList
                        contentList={content.contentList.content.map((item) => (
                          <BodyBaseText numberOfLines={0} key={item}>
                            {item}
                          </BodyBaseText>
                        ))}
                      />
                    ) : null}
                  </View>
                ))}
              </View>
            ) : null}
            {borrowingPowerPage.disclaimer ? (
              <BodyXSText style={styles.disclaimerText}>
                {borrowingPowerPage.disclaimer}
              </BodyXSText>
            ) : null}
          </ScrollView>

          {borrowingPowerPage.footerButton ? (
            <View style={styles.footerButton}>
              <Button
                label={borrowingPowerPage.footerButton.buttonText}
                type={borrowingPowerPage.footerButton.buttonStyle}
                onPress={() => {
                  const buttonOnClick =
                    borrowingPowerPage.footerButton?.buttonOnClick;
                  if (
                    buttonOnClick?.__typename ===
                    Typenames.LaunchPinwheelFlowAction
                  ) {
                    const { name, properties } = parseGQLAnalyticsEvent(
                      buttonOnClick.analyticsEvent as AnalyticsEvent,
                    );
                    void segmentClient.logEvent(name, properties);

                    if (buttonOnClick.shouldLaunchPinwheelSdk) {
                      initiatePinwheel({
                        input: {
                          experience:
                            PinwheelExperienceInput.DirectDepositSwitch,
                          skipIntroScreen,
                        },
                        launchBehavior: skipIntroScreen
                          ? { delayAfterTokenMs: 3000 }
                          : undefined,
                        onOutcome: (result) => {
                          void refetchDdManagementFlow(result);
                          refetch({ requestPolicy: 'cache-and-network' });
                        },
                        onLinkTokenError: handleDdManagementLinkTokenFailure,
                      });
                    } else {
                      router.push(
                        '/move-money/direct-deposit/direct-deposit-management',
                      );
                    }
                  } else if (
                    buttonOnClick?.__typename ===
                    VxlActionTypenames.RouteNavigationAction
                  ) {
                    const { name, properties } = parseGQLAnalyticsEvent(
                      buttonOnClick.analyticsEvent as AnalyticsEvent,
                    );
                    void segmentClient.logEvent(name, properties);
                    // this is a server driven route and can't be type checked at compile time
                    // cast to Href to prevent type error.
                    router.push(buttonOnClick.route as Href);
                  } else {
                    initiatePinwheel();
                  }
                }}
              />
            </View>
          ) : null}
        </View>
      ) : null}

      <ScreenTransitionOverlay
        isLoading={fetching || isPinwheelLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={fetching}
            isPinwheelLoading={isPinwheelLoading}
          />
        }
        ErrorComponent={
          formattedError || isBorrowingPowerPageError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={
                isBorrowingPowerPageError
                  ? borrowingPowerPage.title
                  : (formattedError?.title ?? defaultTitle)
              }
              messageBody={
                isBorrowingPowerPageError
                  ? borrowingPowerPage.message
                  : (formattedError?.message ?? defaultMessage)
              }
              primaryButton={{
                title: retryButtonTitle,
                onPress: () => refetch({ requestPolicy: 'network-only' }),
              }}
              secondaryButton={{
                title: cancelButtonTitle,
                onPress: () => {
                  if (router.canGoBack()) {
                    router.back();
                  }
                },
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentList: {
    paddingTop: theme.spacing.m,
  },
  contentListSubtitle: {
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  disclaimerText: {
    alignSelf: 'stretch',
    color: theme.color.text.secondary,
    paddingTop: theme.spacing.m,
  },
  footerButton: {
    padding: theme.spacing.m,
  },
  parentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    padding: theme.spacing.m,
  },
  subTitleText: { alignSelf: 'stretch' },
  titleText: { alignSelf: 'stretch', paddingBottom: theme.spacing.m },
});

export default IncreaseYourAdvanceLimits;
